import React, { useState, useMemo } from 'react';
import { getStatIcon, getStatColor } from '../../utils/statIconUtils';

export interface TimelineEvent {
  eventId: string;
  matchMinute: number | null;
  eventTimestamp: string;
  statType: { id: string; stat_key: string; display_name: string } | null;
  team: { id: string; name: string } | null;
  primaryPlayer: { id: string; full_name: string } | null;
  videoTimeSeconds: number | null;
  videoId: string | null;
  tags: string[];
}

interface VideoTimelineOverlayProps {
  events: TimelineEvent[];
  videoDuration: number; // Total video duration in seconds
  currentVideoId: string | null; // Current video being displayed
  onEventClick: (videoTimeSeconds: number, videoId: string) => void;
  className?: string;
  rangeBefore?: number; // Seconds before event (default: 10)
  rangeAfter?: number; // Seconds after event (default: 5)
  currentTime?: number; // Current playback time in seconds
}

/**
 * Video Timeline Overlay Component
 * Displays timeline markers on video player for key events
 */
const VideoTimelineOverlay: React.FC<VideoTimelineOverlayProps> = ({
  events,
  videoDuration,
  currentVideoId,
  onEventClick,
  className = '',
  rangeBefore = 10,
  rangeAfter = 5,
  currentTime = 0
}) => {
  // All hooks must be called before any conditional returns
  const [hoveredEventId, setHoveredEventId] = useState<string | null>(null);

  // Filter events for current video and those with video timing
  const relevantEvents = useMemo(() => 
    events.filter(event => 
      event.videoId === currentVideoId && 
      event.videoTimeSeconds !== null && 
      event.videoTimeSeconds !== undefined
    ),
    [events, currentVideoId]
  );

  // Sort events by time
  const sortedEvents = useMemo(() => 
    [...relevantEvents].sort((a, b) => 
      (a.videoTimeSeconds || 0) - (b.videoTimeSeconds || 0)
    ),
    [relevantEvents]
  );

  // Use shared utility functions for icons and colors

  // Calculate tooltip positions to avoid overlaps
  const calculateTooltipOffset = (index: number, eventPositions: Array<{ left: number }>): number => {
    if (index === 0) return 0.75;
    
    // Count how many previous events are close to this one
    let overlapCount = 0;
    const currentPos = eventPositions[index];
    if (!currentPos) return 0.75;
    
    for (let i = 0; i < index; i++) {
      const prevPos = eventPositions[i];
      if (!prevPos) continue;
      
      // Check if positions are close (within 2% of timeline)
      const distance = Math.abs(currentPos.left - prevPos.left);
      if (distance < 2) {
        overlapCount++;
      }
    }
    
    // Base offset + additional offset for each overlapping tooltip
    return 0.75 + (overlapCount * 1.2); // rem units
  };

  // Calculate marker positions with clustering for dense events
  const markerPositions = useMemo(() => {
    if (sortedEvents.length === 0 || videoDuration === 0) {
      return [];
    }

    const MAX_MARKERS_PER_CLUSTER = 5;
    const CLUSTER_THRESHOLD = sortedEvents.length > 100 ? 0.3 : 0.5; // Tighter clustering for many events
    const positions: Array<{ 
      event: typeof sortedEvents[0]; 
      left: number; 
      width: number; 
      isClustered: boolean;
      clusterSize?: number;
    }> = [];
    
    // Helper function to calculate range width
    const calculateRangeWidth = (videoTimeSeconds: number): number => {
      const startTime = Math.max(0, videoTimeSeconds - rangeBefore);
      const endTime = Math.min(videoDuration, videoTimeSeconds + rangeAfter);
      return ((endTime - startTime) / videoDuration) * 100;
    };
    
    // Calculate base positions
    const basePositions = sortedEvents.map(event => ({
      event,
      leftPercent: (event.videoTimeSeconds! / videoDuration) * 100
    }));

    // Group events that are too close together (clustering)
    const clusters: Array<typeof basePositions> = [];
    let currentCluster: typeof basePositions = [];
    
    basePositions.forEach((pos) => {
      if (currentCluster.length === 0) {
        currentCluster.push(pos);
      } else {
        const prevPos = currentCluster[currentCluster.length - 1];
        const timeDiff = pos.leftPercent - prevPos.leftPercent;
        
        // If events are very close, cluster them
        if (timeDiff < CLUSTER_THRESHOLD && currentCluster.length < MAX_MARKERS_PER_CLUSTER) {
          currentCluster.push(pos);
        } else {
          clusters.push(currentCluster);
          currentCluster = [pos];
        }
      }
    });
    if (currentCluster.length > 0) {
      clusters.push(currentCluster);
    }

    // Calculate actual positions
    clusters.forEach(cluster => {
      if (cluster.length === 1) {
        // Single marker - position at its time
        const pos = cluster[0];
        positions.push({
          event: pos.event,
          left: pos.leftPercent,
          width: calculateRangeWidth(pos.event.videoTimeSeconds!),
          isClustered: false
        });
      } else {
        // Clustered markers - position at average time, spread horizontally
        const avgLeftPercent = cluster.reduce((sum, p) => sum + p.leftPercent, 0) / cluster.length;
        const spread = Math.min(cluster.length * 0.2, 1.5); // Max 1.5% spread for cluster
        
        cluster.forEach((pos, idx) => {
          const offset = (idx - (cluster.length - 1) / 2) * (spread / cluster.length);
          positions.push({
            event: pos.event,
            left: Math.max(0, Math.min(100, avgLeftPercent + offset)),
            width: calculateRangeWidth(pos.event.videoTimeSeconds!),
            isClustered: true,
            clusterSize: cluster.length
          });
        });
      }
    });

    return positions;
  }, [sortedEvents, videoDuration, rangeBefore, rangeAfter]);

  // Calculate marker size based on density
  const markerSize = useMemo(() => {
    const baseSize = 24; // Base size in pixels
    const densityFactor = Math.min(1, 50 / sortedEvents.length); // Scale down if many events
    return Math.max(16, baseSize * densityFactor); // Min 16px, max 24px
  }, [sortedEvents.length]);

  // Early return after all hooks
  if (relevantEvents.length === 0 || videoDuration === 0) {
    return null;
  }

  return (
    <div className={`absolute bottom-0 left-0 right-0 h-8 bg-gray-900 bg-opacity-70 overflow-visible ${className}`} style={{ zIndex: 20 }}>
      {/* Timeline bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-600 bg-opacity-50" />
      
      {markerPositions.map((markerData, index) => {
        const { event, left, width, isClustered, clusterSize } = markerData;
        const color = getStatColor(event.statType?.stat_key || null);
        const isHovered = hoveredEventId === event.eventId;
        const tooltipOffset = calculateTooltipOffset(index, markerPositions);
        
        // Check if this marker is currently playing (within range)
        const isPlaying = event.videoTimeSeconds !== null && 
          currentTime >= Math.max(0, event.videoTimeSeconds - rangeBefore) &&
          currentTime <= Math.min(videoDuration, event.videoTimeSeconds + rangeAfter);
        
        // Dynamic marker size based on hover or playing state
        const currentMarkerSize = (isHovered || isPlaying) ? markerSize * 1.5 : markerSize;
        
        return (
          <div
            key={event.eventId}
            className="absolute bottom-0 cursor-pointer z-10"
            style={{ 
              left: `${Math.max(0, Math.min(100, left))}%`,
              transform: 'translateX(-50%)'
            }}
            onMouseEnter={() => setHoveredEventId(event.eventId)}
            onMouseLeave={() => setHoveredEventId(null)}
            onClick={() => {
              if (event.videoTimeSeconds !== null && event.videoId) {
                const startTime = Math.max(0, event.videoTimeSeconds - rangeBefore);
                onEventClick(startTime, event.videoId);
              }
            }}
          >
            {/* Range bar - only show if not clustered or hovered */}
            {(!isClustered || isHovered) && (
              <div 
                className={`absolute bottom-0 ${color} opacity-60 transition-all duration-200 rounded-sm border border-white border-opacity-30 ${
                  isHovered ? 'opacity-80' : ''
                }`}
                style={{
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: `${width}%`,
                  height: '12px',
                  minWidth: '20px'
                }}
              />
            )}
            
            {/* Center marker line with emoji */}
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 flex flex-col items-center" style={{ height: '100%' }}>
              <div className={`w-0.5 ${color} opacity-90 transition-opacity duration-200 ${isHovered ? 'opacity-100' : ''}`} style={{ height: '12px' }} />
              
              {/* Emoji marker at center with smooth animation - positioned at bottom, expands upward on hover */}
              <div 
                className={`absolute bottom-0 bg-gray-900 rounded-full flex items-center justify-center text-xs border transition-all duration-200 ease-out ${
                  isPlaying
                    ? 'bg-opacity-95 shadow-lg border-yellow-400 border-opacity-100 ring-2 ring-yellow-400 ring-opacity-50'
                    : isHovered
                    ? 'bg-opacity-95 shadow-lg border-white border-opacity-80'
                    : 'bg-opacity-80 border-white border-opacity-50'
                }`}
                style={{
                  width: `${currentMarkerSize}px`,
                  height: `${currentMarkerSize}px`,
                  left: '50%',
                  transform: `translate(-50%, ${(isHovered || isPlaying) ? '0' : '50%'}) scale(${(isHovered || isPlaying) ? 1.5 : 1})`,
                  transformOrigin: 'center bottom',
                  transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), width 0.2s cubic-bezier(0.4, 0, 0.2, 1), height 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                {getStatIcon(event.statType?.stat_key || null)}
                {isClustered && clusterSize && clusterSize > 1 && !isHovered && !isPlaying && (
                  <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-[8px] rounded-full w-3 h-3 flex items-center justify-center font-bold">
                    {clusterSize}
                  </span>
                )}
              </div>
            </div>
            
            {/* Tooltip on hover or when playing */}
            {(isHovered || isPlaying) && (
              <div 
                className="absolute pointer-events-none"
                style={{
                  bottom: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  marginBottom: `${tooltipOffset}rem`,
                  zIndex: 100 + index,
                  animation: isHovered ? 'fadeIn 0.2s ease-out' : 'none'
                }}
              >
                <div className={`text-xs rounded px-2 py-1.5 whitespace-nowrap shadow-lg ${
                  isPlaying 
                    ? 'bg-yellow-500 text-gray-900 border-2 border-yellow-400' 
                    : 'bg-gray-900 text-white'
                }`}>
                  <div className="font-medium">{event.statType?.display_name || 'Event'}</div>
                  {event.matchMinute !== null && (
                    <div className={isPlaying ? 'text-gray-700' : 'text-gray-300'}>
                      {event.matchMinute}' minute
                    </div>
                  )}
                  {(event.team || event.primaryPlayer) && (
                    <div className={isPlaying ? 'text-gray-700' : 'text-gray-300'}>
                      {[event.team?.name, event.primaryPlayer?.full_name].filter(Boolean).join(' • ')}
                    </div>
                  )}
                  {isClustered && clusterSize && clusterSize > 1 && (
                    <div className={`text-xs mt-1 ${isPlaying ? 'text-gray-600' : 'text-blue-300'}`}>
                      {clusterSize} events clustered
                    </div>
                  )}
                  {isPlaying && (
                    <div className="text-xs mt-1 font-semibold text-gray-800">
                      ▶ Now Playing
                    </div>
                  )}
                </div>
                {/* Tooltip arrow - centered */}
                <div 
                  className={`absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent ${
                    isPlaying ? 'border-t-yellow-500' : 'border-t-gray-900'
                  }`} 
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default VideoTimelineOverlay;

